import RootModal from "@/components/RootModal/RootModal";
import { UniqueRouteFacility, UniqueRouteLeg } from "@/hooks/useTransport";
import { Skeleton } from "antd";
import { Fragment, useEffect, useState } from "react";
import FacilityLeg from "./FacilityLeg";
import NormalLeg from "./NormalLeg";
import styles from "./RouteWizard.module.scss";
import TransportMode from "./TransportMode";

export default function RouteWizard({
  route: initialRoute,
  departure,
  arrival,
  count,
  isOpen,
  onClose,
  arrivalType,
  loading,
  onSave: parentOnSave,
  isSaving,
}: {
  departure?: UniqueRouteFacility;
  arrival?: UniqueRouteFacility | null;
  route?: UniqueRouteLeg[];
  count?: number;
  isOpen: boolean;
  onClose: () => void;
  arrivalType: "facility" | "customer";
  loading: boolean;
  onSave: (route: UniqueRouteLeg[]) => void;
  isSaving: boolean;
}) {
  const [route, setRoute] = useState<UniqueRouteLeg[]>([]);
  const [validation, setValidation] = useState(false);

  const addLeg = (afterIndex: number) => {
    setRoute((route) => [
      ...route.slice(0, afterIndex + 1),
      { address: "", mode: "", owned: false },
      ...route.slice(afterIndex + 1),
    ]);
  };

  const removeLeg = (index: number) => {
    setRoute((route) => [...route.slice(0, index), ...route.slice(index + 1)]);
  };

  const updateLeg = (index: number, leg: UniqueRouteLeg) => {
    setRoute((route) => [...route.slice(0, index), leg, ...route.slice(index + 1)]);
  };

  useEffect(() => {
    setRoute(
      initialRoute || [
        { address: departure?.address || "", mode: null, owned: false },
        { address: arrival?.address || "", mode: null, owned: false },
      ]
    );
  }, [initialRoute]);

  const legErrors = route.map((leg, index) => {
    return {
      // address must always be a non-empty string, except if arrival facility is null
      address: index === route.length - 1 && arrival === null ? false : !leg.address,
      // mode must always be a non-empty string, except for the last leg (always empty)
      mode: index === route.length - 1 ? false : !leg.mode,
    };
  });

  const onSave = () => {
    if (legErrors.every((error) => !error.address && !error.mode)) {
      parentOnSave(route);
    } else {
      setValidation(true);
    }
  };

  return (
    <>
      <RootModal
        title={
          <span>
            {loading ? "Edit routes" : `Edit routes for ${count} order${count === 1 ? "" : "s"}`}
          </span>
        }
        open={isOpen}
        width={700}
        okText="Save"
        onOk={onSave}
        onCancel={onClose}
        zIndex={7}
        okButtonProps={{ loading: isSaving }}
      >
        {departure === undefined || arrival === undefined ? (
          <Skeleton />
        ) : (
          <div className={styles.legs}>
            {route.map((leg, index) => (
              <Fragment key={index}>
                {index === 0 ? (
                  <FacilityLeg
                    updateLeg={updateLeg}
                    leg={leg}
                    address={departure}
                    index={index}
                    error={validation && legErrors[index].address}
                    totalRoutesCount={count || 0}
                    type={"facility"}
                  />
                ) : index === route.length - 1 ? (
                  <FacilityLeg
                    updateLeg={updateLeg}
                    leg={leg}
                    address={arrival}
                    index={index}
                    error={validation && legErrors[index].address}
                    totalRoutesCount={count || 0}
                    type={arrivalType}
                  />
                ) : (
                  <NormalLeg
                    updateLeg={updateLeg}
                    removeLeg={removeLeg}
                    leg={leg}
                    index={index}
                    error={validation && legErrors[index].address}
                  />
                )}
                {index < route.length - 1 && (
                  <TransportMode
                    addLeg={addLeg}
                    updateLeg={updateLeg}
                    leg={leg}
                    index={index}
                    error={validation && legErrors[index].mode}
                  />
                )}
              </Fragment>
            ))}
          </div>
        )}
      </RootModal>
    </>
  );
}
